"use client";

import React, {
  Suspense,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Card,
  CardContent,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import CloseIcon from "@mui/icons-material/Close";
import { useSnackbar } from "notistack";
import { useQuery } from "@tanstack/react-query";
import dynamic from "next/dynamic";

// import gaService from "@/api/services/ga.service";
import dataService from "@/api/services/data.service";
import feedbackService from "@/api/services/feedback.service";
import GRAPH_DESCRIPTION from "@/constants/descriptions.constant";
import { useFiltersStore } from "@/store/useFilterStore";
import { useMetaTitle } from "@/hooks/useMetaTitle";
// import MapComponent from "@/components/new-ui/stateMapMarkers";
import Board from "./ShivirBoard";
import StatBoard from "./StatBoard";
import FeedbackSmileys from "./FeedbackSmileys";
import { Info } from "lucide-react";
import useDevice from "@/hooks/useDevice";
import gaService from "@/api/services/ga.service";
import ReactMarkdown from "react-markdown";
import {
  formatAiOverviewTitle,
  toTitleCase,
} from "@/components/aiSummaryUtils";

const mapLoader = () => (
  <Skeleton
    variant="rounded"
    sx={{
      width: "100%",
      height: { xs: 320, md: 500 },
      bgcolor: "#1D1930",
      borderRadius: 2,
    }}
  />
);

const MapComponent = dynamic(() => import("./maps/MapComponent"), {
  ssr: false,
  loading: mapLoader,
});

const HeatMap = dynamic(() => import("./maps/HeatMap"), {
  ssr: false,
  loading: mapLoader,
});

// ---------------------- Geographical helpers ----------------------

const TABLE_DESCRIPTIONS = {
  order_volume:
    "This list consists of top Tier 1,2 and 3 cities of India by order volume in the given period. Click on download to view the full list. This will provide a snapshot of opportunities available in top cities.",
  aov:
    "This list consists of top Tier 1,2 and 3 cities of India by Average Order Value in the given period. Click on download to view the full list. This will provide a snapshot of opportunities available in top cities.",
  rto_perc:
    "This list consists of top Tier 1,2 and 3 cities of India by RTO% in the given period. Click on download to view the full list. This will provide a snapshot of RTO performance in top cities.",
  prepaid_perc:
    "This list consists of top Tier 1,2 and 3 cities of India with Prepaid Orders in the given period. Click on download to view the full list. This will provide a snapshot of Prepaid Order distribution in top cities.",
  emerging_cities:
    "This list consists of Emerging cities with high potential in the given period. Emerging cities are cities where there was a rapid growth of order volume compared to other cities. Click on download to view the full list.",
  top_pincode:
    "This list gives the top pincodes where RTO is a major problem. This list will you to optimise your last mile operations. Click on download to view the full list.",
} as const;

const TAB_LIST = [
  { label: "AOV", id: "citytier_aov" },
  { label: "Order Volume", id: "citytier_order_volume" },
  { label: "RTO %", id: "citytier_rto_percentage" },
  { label: "Prepaid %", id: "citytier_prepaid_percentage" },
  { label: "Pincodes", id: "top_rto_picode" },
  { label: "Emerging Cities", id: "emerging_cities" },
] as const;

// Hook to fetch geo data with TanStack Query using your global filters
export function useGeoQuery() {
  const { selectedCategory, selectedDateRange } = useFiltersStore();

  const catId =
    selectedCategory?.id != null && !Number.isNaN(Number(selectedCategory.id))
      ? Number(selectedCategory.id)
      : undefined;

  // selectedDateRange key is already LAST_MONTH | LAST_3_MONTHS | LAST_6_MONTHS (pType)
  const pType = selectedDateRange || "LAST_MONTH";

  return useQuery({
    queryKey: ["geo", pType, catId ?? null],
    queryFn: () =>
      dataService.getGeoGraphicalData({
        pType,
        categoryId: catId,
      }),
    select: (resp: { data: Record<string, unknown> }) => resp?.data ?? {},
    // keepPreviousData: true,
  });
}

// ---------------------- Component ----------------------

interface ShivirDataBoardProps {
  pageType:
    | "Dashboard"
    | "OrderTrends"
    | "RtoTrends"
    | "CategoryTrends"
    | "BuyerStats"
    | "GeographicalTrends";
  summaryData: any | null;
  heatmapData?: any | null;
}
const RADIAL_STYLE: React.CSSProperties = { height: 420 };
const ShivirDataBoard: React.FC<ShivirDataBoardProps> = ({
  pageType,
  summaryData,
  heatmapData,
}) => {
  const stableData = useMemo(
    () => summaryData ?? (undefined as undefined | Record<string, unknown>),
    [summaryData]
  );

  const loading = !stableData;

  // --- memoized content blocks (stable unless real deps change) ---
  const dashboardJSX = useMemo(() => {
    if (pageType !== "Dashboard") return null;

    return (
      <div className="cstmChartsLayout">
        {/* Row 1: Orders Trends + AOV Distribution */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 xl:gap-8 mb-4 xl:mb-8">
          <Board
            className="md:col-span-12 darkBgWithBackdrop lg:col-span-7"
            type="stacked_bar_chart"
            disableTab
            title="Orders Trends"
            data={stableData?.ordersOverTime}
            description={
              <TooltipDescription
                text={GRAPH_DESCRIPTION.ordersOverTime}
                heading="Orders Trends"
              />
            }
            secondaryDescription={
              <Stack
                direction={"row"}
                gap={0.5}
                alignItems={"center"}
                sx={{
                  mt: 2,
                }}
              >
                <Info color="#ffffff50" size={16} />
                &nbsp;Value of 100 represents highest order volume in the given
                time period
              </Stack>
            }
            yAxisUnit="Percentile"
          />

          <Board
            className="md:col-span-12 darkBgWithBackdrop lg:col-span-5"
            type="horizontal_bar_chart"
            disableTab
            title="Order Value Distribution"
            data={stableData?.aovDistribution}
            description={
              <TooltipDescription
                text={GRAPH_DESCRIPTION.OrderValueDistribution}
                heading="Order Value Distribution"
              />
            }
            yAxisUnit="Percentage"
          />
        </div>

        {/* Row 2 */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 xl:gap-8 mb-4 xl:mb-8 lastmonthAlignment">
          <StatBoard
            className="darkBgWithBackdrop"
            title="GMV Trends"
            data={{value : stableData?.gmvGenerated}}
          />
          <StatBoard
            className="darkBgWithBackdrop"
            title="Discounted Orders %"
            data={stableData?.discountedOrders}
          />
          <StatBoard
            className="darkBgWithBackdrop"
            title="AOV"
            data={stableData?.aovPercentage}
          />
          <StatBoard
            className="darkBgWithBackdrop"
            title="RTO %"
            data={stableData?.rtoPercentage}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 xl:gap-8 mb-4 xl:mb-8">
          <Board
            className="darkBgWithBackdrop"
            type="category_data_balls"
            title="Top Selling Categories"
            data={stableData?.topParentCategories}
            disableTab
            clickable
            description={
              <TooltipDescription
                text={GRAPH_DESCRIPTION.categoryInsights}
                heading="Top Selling Categories"
              />
            }
          />

          <Board
            className="darkBgWithBackdrop productCard"
            type="category_with_products_table"
            title="Top Sub Categories"
            data={stableData?.topSubCategories}
            disableTab
            description={
              <TooltipDescription
                text={GRAPH_DESCRIPTION.subCategoryInsights}
                heading="Top Sub Categories"
              />
            }
            yAxisUnit="subCategory"
          />
          <Board
            className="darkBgWithBackdrop productCard"
            type="category_with_products_table"
            title="Popular Products"
            data={stableData?.topProducts}
            disableTab
            description={
              <TooltipDescription
                text={GRAPH_DESCRIPTION.productInsights}
                heading="Popular Products"
              />
            }
            yAxisUnit="products"
          />
        </div>
        {/* Row 3 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 xl:gap-8 mb-4 xl:mb-8">
          <Board
            className="darkBgWithBackdrop"
            type="pie_custom"
            title="Prepaid Vs COD Orders"
            disableTab
            data={stableData?.prepaidCodSplit}
            description={
              <TooltipDescription
                text={GRAPH_DESCRIPTION.PrepaidCOD}
                heading="Prepaid Vs COD Orders"
              />
            }
            yAxisUnit="COD"
            style={RADIAL_STYLE}
          />
          <Board
            className="darkBgWithBackdrop buyerSegment"
            type="radial_graph"
            title="Buyer Segments"
            disableTab
            data={stableData?.buyerSegments}
            description={
              <TooltipDescription
                text={GRAPH_DESCRIPTION.BuyerSegments}
                heading="Buyer Segments"
              />
            }
            yAxisUnit="Segments"
            style={RADIAL_STYLE}
          />
          <Board
            className="darkBgWithBackdrop"
            type="pie_custom"
            title="New Vs Repeat buyers"
            disableTab
            data={stableData?.newVsRepeatBuyerSplit}
            description={
              <TooltipDescription
                text={GRAPH_DESCRIPTION.NewRepeatBuyer}
                heading="New Vs Repeat buyers"
              />
            }
            yAxisUnit="Repeat"
            style={RADIAL_STYLE}
          />
        </div>

        {/* Row 4 */}
        <div className="grid grid-cols-1 gap-4 xl:gap-8 mb-4 xl:mb-8">
          <Board
            className="darkBgWithBackdrop"
            type="heat_map_with_data"
            title="Order Placement Timings"
            data={stableData?.ordersByTimeOfDay}
            description={
              <TooltipDescription
                text={GRAPH_DESCRIPTION.OrderPlacementTimings}
                heading="Order Placement Timings"
              />
            }
            disableTab
          />
        </div>
      </div>
    );
  }, [pageType, stableData]);


  const isMobile = useDevice()
  const rtoTrendsJSX = useMemo(() => {
    if (pageType !== "RtoTrends") return null;

    return (
      <div className="cstmChartsLayout">
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-5 xl:gap-8 mb-8 lastmonthAlignment">
          <div className="xl:col-span-5 col-span-12">
            <div className="xl:sticky xl:top-20 self-start max-h-[calc(100vh-5rem)]">
            <HeatMap
              heatMapData={heatmapData}
              showData={true}
              dataLabel="RTO"
              dataUnit={"%"}
            />
          </div>
          </div>

          <div className="xl:col-span-7 col-span-12 flex flex-col gap-4">
            <Board
              className="darkBgWithBackdrop"
              type="horizontal_bar_chart"
              disableTab
              title="RTO Trends"
              data={stableData?.rtoOverTime}
              description={
                <TooltipDescription
                  text={GRAPH_DESCRIPTION.rtoOverTime}
                  heading="RTO Trends"
                />
              }
              yAxisUnit="Percentage"
            />
            <Stack direction={isMobile ? "column" : "row"} gap={2}>
              <StatBoard
              className="w-1/2"
              title="RTO %"
              data={stableData?.rtoPercentage}
            />


              <StatBoard
             className="w-1/2"
            title="AOV"
            data={stableData?.aovPercentage}
          />
            </Stack>
            
          </div>
        </div>
      </div>
    );
  }, [pageType, heatmapData, stableData]);

  const orderTrendsJSX = useMemo(() => {
    if (pageType !== "OrderTrends") return null;

    return (
      <div className="cstmChartsLayout">
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-4 xl:gap-8 mb-4 xl:mb-8">
          <div className="xl:col-span-5 col-span-12">
            <div className="xl:sticky xl:top-20 self-start max-h-[calc(100vh-5rem)]">
              <HeatMap
              heatMapData={heatmapData}
              showData
              dataLabel="Orders"
              dataUnit="%"
              />
            </div>
          </div>

          <div className="xl:col-span-7 col-span-12 flex flex-col gap-4">
            <Board
              className="darkBgWithBackdrop"
              type="line_graph"
              disableTab
              title="Orders Trends"
              data={stableData?.ordersOverTime}
              description={
                <TooltipDescription
                  text={GRAPH_DESCRIPTION.ordersOverTime}
                  heading="Orders Trends"
                />
              }
              secondaryDescription={
                <Stack
                  direction={"row"}
                  gap={0.5}
                  alignItems={"center"}
                  sx={{
                    mt: 2,
                  }}
                >
                  <Info color="#ffffff50" size={16} />
                  &nbsp;Value of 100 represents highest order volume in the
                  given time period
                </Stack>
              }
              yAxisUnit="Percentile"
            />

            <Board
              className="darkBgWithBackdrop"
              type="heat_map_with_data"
              title="Order Placement Timings"
              data={stableData?.ordersByTimeOfDay}
              description={
                <TooltipDescription
                  text={GRAPH_DESCRIPTION.OrderPlacementTimings}
                  heading="Order Placement Timings"
                />
              }
              disableTab
            />

            <Board
              className="darkBgWithBackdrop"
              type="horizontal_bar_chart"
              disableTab
              title="Order Value Distribution"
              data={stableData?.aovDistribution}
              description={
                <TooltipDescription
                  text={GRAPH_DESCRIPTION.OrderValueDistribution}
                  heading="Order Value Distribution"
                />
              }
              yAxisUnit="Percentage"
            />
          </div>
        </div>
      </div>
    );
  }, [pageType, heatmapData, stableData]);

  const buyerStatsJSX = useMemo(() => {
    if (pageType !== "BuyerStats") return null;

    return (
      <div className="cstmChartsLayout">
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-4 xl:gap-8 mb-4 xl:mb-8">
          <div className="xl:col-span-5 col-span-12">
            <div className="lg:sticky lg:top-20">
              <HeatMap />
            </div>
          </div>

          <div className="xl:col-span-7 col-span-12 flex flex-col gap-4">
            <Board
              className="darkBgWithBackdrop buyerSegment"
              type="radial_graph"
              title="Buyer Segments"
              disableTab
              data={stableData?.buyerSegments}
              description={
                <TooltipDescription
                  text={GRAPH_DESCRIPTION.BuyerSegments}
                  heading="Buyer Segments"
                />
              }
              style={{
                minHeight : "300px"
              }}
              yAxisUnit="Segments"
            />

            <Board
              className="darkBgWithBackdrop"
              type="pie_custom"
              title="Prepaid / COD Split"
              disableTab
              data={stableData?.prepaidCodSplit}
              description={
                <TooltipDescription
                  text={GRAPH_DESCRIPTION.PrepaidCOD}
                  heading="Prepaid/COD Split"
                />
              }
               style={{
                minHeight : "300px"
              }}
              yAxisUnit="COD"
            />

            <Board
              className="darkBgWithBackdrop"
              type="pie_custom"
              title="New/Repeat buyers split"
              disableTab
              data={stableData?.newVsRepeatBuyerSplit}
              description={
                <TooltipDescription
                  text={GRAPH_DESCRIPTION.NewRepeatBuyer}
                  heading="New/Repeat buyers"
                />
              }
               style={{
                minHeight : "300px"
              }}
              yAxisUnit="Repeat"
            />
          </div>
        </div>
      </div>
    );
  }, [pageType, stableData]);

  const categoryTrendsJSX = useMemo(() => {
    if (pageType !== "CategoryTrends") return null;

    return (
      <div className="cstmChartsLayout">
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-4 xl:gap-8 mb-4 xl:mb-8">
          <div className="xl:col-span-5 col-span-12">
            <div className="lg:sticky lg:top-20">
              <HeatMap />
            </div>
          </div>

          <div className="xl:col-span-7 col-span-12 flex flex-col gap-4">
            <Board
              className="darkBgWithBackdrop"
              type="category_data_balls"
              title="Top Selling Categories"
              data={stableData?.topParentCategories}
              disableTab
              clickable
              description={
                <TooltipDescription
                  text={GRAPH_DESCRIPTION.categoryInsights}
                  heading="Top Selling Categories"
                />
              }
            />

            <div className="grid grid-cols-1 xl:grid-cols-1 gap-4 xl:gap-8">
              <Board
                className="darkBgWithBackdrop productCard"
                type="category_with_products_table"
                title="Top Sub Categories"
                data={stableData?.topSubCategories}
                disableTab
                description={
                  <TooltipDescription
                    text={GRAPH_DESCRIPTION.subCategoryInsights}
                    heading="Top Sub Categories"
                  />
                }
                yAxisUnit="subCategory"
              />
              <Board
                className="darkBgWithBackdrop productCard"
                type="category_with_products_table"
                title="Popular Products"
                data={stableData?.topProducts}
                disableTab
                description={
                  <TooltipDescription
                    text={GRAPH_DESCRIPTION.productInsights}
                    heading="Popular Products"
                  />
                }
                yAxisUnit="products"
              />
            </div>
          </div>
        </div>
      </div>
    );
  }, [pageType, stableData]);

  const geographicalTrendsJSX = useMemo(() => {
    if (pageType !== "GeographicalTrends") return null;
    return <GeographicalTrends />;
  }, [pageType]);

  // --- Choose which block to render ---
  const mainContent = useMemo(() => {
    switch (pageType) {
      case "Dashboard":
        return dashboardJSX;
      case "OrderTrends":
        return orderTrendsJSX;
      case "RtoTrends":
        return rtoTrendsJSX;
      case "CategoryTrends":
        return categoryTrendsJSX;
      case "BuyerStats":
        return buyerStatsJSX;
      case "GeographicalTrends":
        return geographicalTrendsJSX;
      default:
        return null;
    }
  }, [
    pageType,
    dashboardJSX,
    orderTrendsJSX,
    rtoTrendsJSX,
    categoryTrendsJSX,
    buyerStatsJSX,
    geographicalTrendsJSX,
  ]);

  return (
    <Suspense
      fallback={
        <Loader
          variant={pageType === "Dashboard" ? "dashboard" : "map-board"}
        />
      }
    >
      <div>
        {loading ? (
          <Loader
            variant={pageType === "Dashboard" ? "dashboard" : "map-board"}
          />
        ) : (
          <>
            {mainContent}
            <FeedbackSmileys />
          </>
        )}
      </div>
    </Suspense>
  );
};

export default React.memo(ShivirDataBoard);

// ---------------------- Dashboard AI Summary ----------------------

type DashboardAISummaryProps = {
  summaryData: Record<string, any> | null;
};

export const DashboardAISummary: React.FC<DashboardAISummaryProps> = ({
  summaryData,
}) => {
  const {
    selectedState,
    selectedCity,
    selectedDateRange,
  } = useFiltersStore();

  const metaTitle = useMetaTitle("Shiprocket Trends Report");
  const [expanded, setExpanded] = useState(false);

  if (!summaryData) return null;

  const numberOrZero = (value: unknown): number => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  };

  const directionFromDelta = (
    delta: number
  ): "positive" | "negative" | "stable" => {
    if (delta > 0) return "positive";
    if (delta < 0) return "negative";
    return "stable";
  };

  const formatPercent = (value: number): string =>
    `${Math.round(Math.abs(value))}%`;

  const timeDisplay =
    selectedDateRange === "LAST_MONTH"
      ? "Last Month"
      : selectedDateRange === "LAST_QUARTER"
      ? "Last 3 Months"
      : selectedDateRange === "LAST_HALF_YEAR"
      ? "Last 6 Months"
      : "Selected Period";

  const stateName = toTitleCase(selectedState?.label ?? "India");
  const cityName =
    selectedCity && selectedCity.id !== "ALL"
      ? toTitleCase(selectedCity.label)
      : null;
  const regionDisplay = cityName || stateName;
  const aiOverviewTitle = formatAiOverviewTitle({
    title: metaTitle,
    city: selectedCity?.label,
    state: selectedState?.label,
  });

  const gmvDelta = numberOrZero(summaryData.gmvGenerated);
  const gmv_trend_direction = directionFromDelta(gmvDelta);
  const gmv_percentage = formatPercent(gmvDelta);

  const discounted = summaryData.discountedOrders || {};
  const discountedValue = numberOrZero(discounted.value);
  const discountedDiff = numberOrZero(discounted.difference);
  const discounted_orders_percentage = `${Math.round(discountedValue)}%`;
  const discounted_orders_trend_direction =
    directionFromDelta(discountedDiff);
  const discounted_orders_trend_percentage = formatPercent(
    discountedDiff
  );

  const aov = summaryData.aovPercentage || {};
  const aovValue = numberOrZero(aov.value);
  const aovDiff = numberOrZero(aov.difference);
  const aov_value = `${Math.round(aovValue)}`;
  const aov_trend_direction = directionFromDelta(aovDiff);
  const aov_trend_percentage = formatPercent(aovDiff);

  const rto = summaryData.rtoPercentage || {};
  const rtoValue = numberOrZero(rto.value);
  const rtoDiff = numberOrZero(rto.difference);
  const rto_percentage = `${Math.round(rtoValue)}%`;
  const rto_trend_direction = directionFromDelta(rtoDiff);
  const rto_trend_percentage = formatPercent(rtoDiff);

  const ordersOverTime = summaryData.ordersOverTime || {};
  const ootXAxis: string[] = ordersOverTime?.xAxis?.data ?? [];
  const ootSeries = ordersOverTime?.yAxis;
  const ootOrdersSeries: number[] = Array.isArray(ootSeries)
    ? ootSeries?.[0]?.data ?? []
    : ootSeries?.data ?? [];
  let highest_order_period = "the selected period";
  if (ootXAxis.length && ootOrdersSeries.length) {
    let maxIdx = 0;
    let maxVal = ootOrdersSeries[0] ?? 0;
    ootOrdersSeries.forEach((v, i) => {
      if (v > maxVal) {
        maxVal = v;
        maxIdx = i;
      }
    });
    highest_order_period = ootXAxis[maxIdx] ?? highest_order_period;
  }

  const aovDist = summaryData.aovDistribution || {};
  const aovLabels: string[] = aovDist?.xAxis?.data ?? [];
  const aovValues: number[] = aovDist?.yAxis?.data ?? [];
  const aovSlots = aovLabels.map((label, idx) => ({
    label,
    value: aovValues[idx] ?? 0,
  }));
  aovSlots.sort((a, b) => b.value - a.value);
  const stripCurrencyRange = (label: string): string =>
    label.replace(/₹/g, "").trim();
  const most_popular_price_range = aovSlots[0]
    ? stripCurrencyRange(aovSlots[0].label)
    : "";
  const second_popular_price_range = aovSlots[1]
    ? stripCurrencyRange(aovSlots[1].label)
    : "";
  const third_popular_price_range = aovSlots[2]
    ? stripCurrencyRange(aovSlots[2].label)
    : "";

  const topParent = summaryData.topParentCategories?.data ?? [];
  const top_category_name = topParent[0]?.name ?? "";
  const top_category_percentile =
    topParent[0]?.value != null
      ? String(Math.round(numberOrZero(topParent[0].value)))
      : "";
  const second_category_name = topParent[1]?.name ?? "";
  const third_category_name = topParent[2]?.name ?? "";

  const topSubs = summaryData.topSubCategories?.data ?? [];
  const top_sub_category_1 = topSubs[0]?.name ?? "";
  const top_sub_category_1_value =
    topSubs[0]?.value != null
      ? String(Number(topSubs[0].value).toFixed(1))
      : "";
  const top_sub_category_2 = topSubs[1]?.name ?? "";
  const top_sub_category_2_value =
    topSubs[1]?.value != null
      ? String(Number(topSubs[1].value).toFixed(1))
      : "";
  const top_sub_category_3 = topSubs[2]?.name ?? "";
  const top_sub_category_3_value =
    topSubs[2]?.value != null
      ? String(Number(topSubs[2].value).toFixed(1))
      : "";
  const parent_category_name =
    topSubs[0]?.parentCategory ??
    top_category_name ??
    "Top Categories";

  const topProducts = summaryData.topProducts?.data ?? [];
  const popular_product_1 = topProducts[0]?.name ?? "";
  const popular_product_1_value =
    topProducts[0]?.value != null
      ? String(Number(topProducts[0].value).toFixed(1))
      : "";
  const popular_product_2 = topProducts[1]?.name ?? "";
  const popular_product_2_value =
    topProducts[1]?.value != null
      ? String(Number(topProducts[1].value).toFixed(1))
      : "";
  const popular_product_3 = topProducts[2]?.name ?? "";
  const popular_product_3_value =
    topProducts[2]?.value != null
      ? String(Number(topProducts[2].value).toFixed(1))
      : "";

  const paySplit = summaryData.prepaidCodSplit || {};
  const payTitles: string[] = paySplit.titles ?? [];
  const payValues: number[] = paySplit.data ?? [];
  const prepaidIdx = payTitles.findIndex((t) =>
    t.toLowerCase().includes("prepaid")
  );
  const codIdx = payTitles.findIndex((t) =>
    t.toLowerCase().includes("cod")
  );
  const prepaidVal =
    prepaidIdx >= 0 ? numberOrZero(payValues[prepaidIdx]) : 0;
  const codVal = codIdx >= 0 ? numberOrZero(payValues[codIdx]) : 0;
  const percentage_prepaid = String(Math.round(prepaidVal));
  const percentage_cod = String(Math.round(codVal));
  const dominant_payment_method =
    prepaidVal >= codVal ? "prepaid" : "COD";

  const buyerSegments = summaryData.buyerSegments || {};
  const segTitles: string[] = buyerSegments.titles ?? [];
  const segValues: number[] = buyerSegments.data ?? [];
  const segPairs = segTitles.map((title, idx) => ({
    title,
    value: numberOrZero(segValues[idx]),
  }));
  segPairs.sort((a, b) => b.value - a.value);
  const dominant_buyer_segment = segPairs[0]?.title ?? "";
  const dominant_buyer_segment_percentage = String(
    Math.round(segPairs[0]?.value ?? 0)
  );
  const second_buyer_segment = segPairs[1]?.title ?? "";
  const second_buyer_segment_percentage = String(
    Math.round(segPairs[1]?.value ?? 0)
  );
  const third_buyer_segment = segPairs[2]?.title ?? "";
  const third_buyer_segment_percentage = String(
    Math.round(segPairs[2]?.value ?? 0)
  );

  const newRepeat = summaryData.newVsRepeatBuyerSplit || {};
  const nrTitles: string[] = newRepeat.titles ?? [];
  const nrValues: number[] = newRepeat.data ?? [];
  const newIdx = nrTitles.findIndex((t) =>
    t.toLowerCase().includes("new")
  );
  const repeatIdx = nrTitles.findIndex((t) =>
    t.toLowerCase().includes("repeat")
  );
  const percentage_new_buyers = String(
    Math.round(
      newIdx >= 0 ? numberOrZero(nrValues[newIdx]) : 0
    )
  );
  const percentage_repeat_buyers = String(
    Math.round(
      repeatIdx >= 0 ? numberOrZero(nrValues[repeatIdx]) : 0
    )
  );

  const timeOfDay = summaryData.ordersByTimeOfDay || {};
  const days: string[] = timeOfDay.xAxis ?? [];
  const slots: string[] = timeOfDay.yAxis ?? [];
  const matrix: number[][] = timeOfDay.data ?? [];

  let peak_time_slot = "";
  let peak_day_of_week = "";
  let highest_intensity_slot = "";
  let lowest_time_slot = "";

  if (slots.length && days.length && matrix.length) {
    let maxVal = -Infinity;
    let maxSlotIdx = 0;
    let maxDayIdx = 0;
    matrix.forEach((row, slotIdx) => {
      row.forEach((val, dayIdx) => {
        if (val > maxVal) {
          maxVal = val;
          maxSlotIdx = slotIdx;
          maxDayIdx = dayIdx;
        }
      });
    });

    peak_time_slot = slots[maxSlotIdx] ?? "";
    highest_intensity_slot = peak_time_slot;
    const dayCode = days[maxDayIdx] ?? "";
    const dayMap: Record<string, string> = {
      SUN: "Sunday",
      MON: "Monday",
      TUE: "Tuesday",
      WED: "Wednesday",
      THU: "Thursday",
      FRI: "Friday",
      SAT: "Saturday",
    };
    peak_day_of_week = dayMap[dayCode] ?? dayCode;

    let minAvg = Infinity;
    let minSlotIdx = 0;
    matrix.forEach((row, slotIdx) => {
      if (!row.length) return;
      const sum = row.reduce((acc, v) => acc + v, 0);
      const avg = sum / row.length;
      if (avg < minAvg) {
        minAvg = avg;
        minSlotIdx = slotIdx;
      }
    });
    lowest_time_slot = slots[minSlotIdx] ?? "";
  }

  const template = `# [Meta Title from Sheet]

## Overview
This comprehensive eCommerce analytics report provides detailed insights into **[State/City] eCommerce trends** for the **[Time Range Display]** period. The report analyzes order patterns, product performance, customer behavior, and payment preferences to help businesses understand market dynamics and optimize their eCommerce strategies in the region.

## Orders Trends Analysis
Let's begin by examining the order patterns over the selected period. The data shows how order volumes have evolved in time, with **[highest_order_period]** recording the highest order volume. We can also see the proportion of discounted orders throughout this period, which provides insight into promotional activity and customer acquisition strategies.

## Order Value Distribution Analysis
Understanding the price sensitivity of your customers is crucial. The order value distribution reveals that the **₹[most_popular_price_range]** price range is the most popular segment, accounting for the largest share of orders. This is followed by the **₹[second_popular_price_range]** and **₹[third_popular_price_range]** brackets, showing clear customer preferences in terms of purchase value.

## Critical Business Metrics
Now let's look at the critical metrics that define business health.

The Gross Merchandise Value (GMV) shows a **[gmv_trend_direction]** trend of **[gmv_percentage]** compared to the last 30 days. The percentage of discounted orders is currently at **[discounted_orders_percentage]**, showing a **[discounted_orders_trend_direction]** trend of **[discounted_orders_trend_percentage]** compared to the last 30 days. The Average Order Value (AOV) is **₹[aov_value]**, indicating a **[aov_trend_direction]** trend of **[aov_trend_percentage]** compared to the last 30 days. Finally, the Return to Origin (RTO) rate is **[rto_percentage]**, with a **[rto_trend_direction]** trend of **[rto_trend_percentage]** compared to the last 30 days.

## Top Selling Categories Performance
The category performance reveals important insights about market demand. The leading category is **[top_category_name]**, which ranks in the **[top_category_percentile]th** percentile. Following this are **[second_category_name]** and **[third_category_name]**, which together represent the primary focus areas for your customer base.

## Top Sub-Categories and Product Analysis
Diving deeper into category performance, the most popular sub-categories are **[top_sub_category_1]** with a value of **[top_sub_category_1_value]**, **[top_sub_category_2]** with **[top_sub_category_2_value]**, and **[top_sub_category_3]** with **[top_sub_category_3_value]**. These sub-categories are all nested within the **[parent_category_name]** parent category.

At the product level, the items driving the most sales are **[popular_product_1]** with a value of **[popular_product_1_value]**, **[popular_product_2]** with **[popular_product_2_value]**, and **[popular_product_3]** with **[popular_product_3_value]**. These products span across different categories, indicating diverse customer interests.

## Payment Methods and Customer Preferences
Customer payment preferences tell us about trust and convenience factors. **[percentage_prepaid]%** of orders are placed using prepaid methods, while **[percentage_cod]%** prefer Cash on Delivery (COD). This distribution indicates a strong preference for **[dominant_payment_method]** in this region.

## Buyer Segments and Behavioral Analysis
The customer base can be segmented into distinct behavioral groups. The largest segment is **[dominant_buyer_segment]** with **[dominant_buyer_segment_percentage]%** of the customer base, followed by **[second_buyer_segment]** at **[second_buyer_segment_percentage]%** and **[third_buyer_segment]** at **[third_buyer_segment_percentage]%**. Understanding these segments helps in tailoring marketing and product strategies.

## Customer Retention and Loyalty Metrics
Customer retention is a key indicator of business health. **[percentage_new_buyers]%** of the customers are new to the platform, while **[percentage_repeat_buyers]%** are returning customers. 

## Order Placement Timings and Shopping Patterns
Finally, understanding when customers prefer to shop is essential for inventory and customer service planning. The data shows that the peak time for placing orders is between **[peak_time_slot]** on **[peak_day_of_week]**, with the highest concentration of orders in the **[highest_intensity_slot]** range. The activity is notably lower during the **[lowest_time_slot]** period, which is typical for most eCommerce platforms.`;

  const replacements: Record<string, string> = {
    "Meta Title from Sheet": aiOverviewTitle,
    "State/City": regionDisplay,
    "Time Range Display": timeDisplay,
    highest_order_period,
    most_popular_price_range,
    second_popular_price_range,
    third_popular_price_range,
    gmv_trend_direction,
    gmv_percentage,
    discounted_orders_percentage,
    discounted_orders_trend_direction,
    discounted_orders_trend_percentage,
    aov_value,
    aov_trend_direction,
    aov_trend_percentage,
    rto_percentage,
    rto_trend_direction,
    rto_trend_percentage,
    top_category_name,
    top_category_percentile,
    second_category_name,
    third_category_name,
    top_sub_category_1,
    top_sub_category_1_value,
    top_sub_category_2,
    top_sub_category_2_value,
    top_sub_category_3,
    top_sub_category_3_value,
    parent_category_name,
    popular_product_1,
    popular_product_1_value,
    popular_product_2,
    popular_product_2_value,
    popular_product_3,
    popular_product_3_value,
    percentage_prepaid,
    percentage_cod,
    dominant_payment_method,
    dominant_buyer_segment,
    dominant_buyer_segment_percentage,
    second_buyer_segment,
    second_buyer_segment_percentage,
    third_buyer_segment,
    third_buyer_segment_percentage,
    percentage_new_buyers,
    percentage_repeat_buyers,
    peak_time_slot,
    peak_day_of_week,
    highest_intensity_slot,
    lowest_time_slot,
  };

  let filled = template;
  Object.entries(replacements).forEach(([key, value]) => {
    const safeVal = value ?? "";
    filled = filled.split(`[${key}]`).join(safeVal);
  });

  return (
    <Box
      sx={{
        border: "0.85px solid #3636364D",
        background: "#161616",
        borderRadius: 1,
        p: 3,
      }}
    >
      <Typography
        variant="h6"
        sx={{ color: "#fff", fontWeight: 600, mb: 2,  display: "flex", alignItems: "center", gap:1 }}
      >
        <img src="https://sr-cdn-1.shiprocket.in/sr-promise/images/llm_overview_trends.svg" alt="AI Overview Icon"></img>
        AI Overview
      </Typography>
      <Box
        sx={{
          position: "relative",
          maxHeight: expanded ? 420 : 180,
          overflow: expanded ? "auto" : "hidden",
          transition: "max-height 0.3s ease",
        }}
      >
        <ReactMarkdown
          components={{
            h1: ({ node, ...props }) => (
              <Typography
                variant="h1"
                sx={{ color: "#fff", fontWeight: 600, mb: 1 , fontSize : '1.3rem'}}
                {...props}
              />
            ),
            h2: ({ node, ...props }) => (
              <Typography
                variant="subtitle1"
               sx={{ color: "#fff", fontWeight: 600, mt: 2, mb: 1, fontSize: '1.1rem' }}
                {...props}
              />
            ),
            p: ({ node, ...props }) => (
              <Typography
                variant="body2"
                sx={{ color: "#E0E0E0", mb: 1.5 }}
                {...props}
              />
            ),
          }}
        >
          {filled}
        </ReactMarkdown>
        {!expanded && (
          <Box
            sx={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 0,
              height: 48,
              background:
                "linear-gradient(180deg, rgba(22,22,22,0) 0%, #161616 100%)",
            }}
          />
        )}
      </Box>
      <Button
        variant="text"
        size="small"
        onClick={() => setExpanded((prev) => !prev)}
        sx={{
          mt: 1,
          color: "#B0B0B0",
          textTransform: "none",
          px: 0,
          "&:hover": { background: "transparent", color: "#FFFFFF" },
        }}
      >
        {expanded ? "Show less" : "Read full summary"}
      </Button>
    </Box>
  );
};

// ---------------------- Tooltip/Loader ----------------------

const TooltipDescription = (props: { heading: string; text: string }) => (
  // gaService.gaHoverOnOrdertrendsQuestion(props.heading),
  <>
    <h3 className="font-bold mb-1">{props.heading}</h3> {props.text}
  </>
);

export const Loader = ({ variant }: { variant: "dashboard" | "map-board" }) => {

  if (variant === "dashboard") {
    return (
      <div className="cstmChartsLayout">
        {/* Top Section */}
        <div className="flex flex-col lg:flex-row gap-4 mb-4 xl:gap-8 xl:mb-8 w-full">
          <Skeleton
            variant="rectangular"
            width="100%"
            height={300}
            className="lg:w-3/5 h-72 rounded-lg custom-skeleton"
          />
          <Skeleton
            variant="rectangular"
            width="100%"
            height={300}
            className="lg:w-2/5 h-72 rounded-lg custom-skeleton"
          />
        </div>

        {/* Middle Section */}
        <div className="flex flex-col lg:flex-row gap-4 mb-4 xl:gap-8 xl:mb-8 w-full">
          {[...Array(4)].map((_, idx) => (
            <Skeleton
              key={idx}
              variant="rectangular"
              width="100%"
              height={300}
              className="lg:w-1/4 h-52 rounded-lg custom-skeleton"
            />
          ))}
        </div>

        {/* Bottom Section */}
        <div className="flex flex-col gap-4 mb-4 w-full xl:gap-8 xl:mb-8">
          <Skeleton
            variant="rectangular"
            width="100%"
            height={300}
            className="h-72 rounded-lg custom-skeleton"
          />
        </div>
      </div>
    );
  }

  // map-board variant
  return (
    <div className="cstmChartsLayout w-full">
      <div className="flex flex-col lg:flex-row gap-4 mb-4 xl:gap-8 xl:mb-8 w-full">
        <Skeleton
          variant="rectangular"
          width="100%"
          height={600}
          className="lg:w-5/12 h-[700px] rounded-lg custom-skeleton"
        />
        <div className="flex flex-col gap-4 w-full">
          <Skeleton
            variant="rectangular"
            width="100%"
            height={400}
            className="h-[500px] rounded-lg custom-skeleton"
          />
          <Skeleton
            variant="rectangular"
            width="100%"
            height={200}
            className="h-52 rounded-lg custom-skeleton"
          />
        </div>
      </div>
    </div>
  );
};


// ---------------------- GeographicalTrends (no LocationContext) ----------------------

const GeographicalTrends: React.FC = () => {
  const { data: geoData = {} as any, isLoading } = useGeoQuery();
  const [currTab, setCurrTab] = useState<string>("citytier_aov");
  const animatedDivRef = useRef<HTMLDivElement>(null);
  const [animationClass, setAnimationClass] = useState(
    "animate__animated animate__faster animate__fadeInUpBig"
  );

  // table card refs for intersection control (kept as-is in case you use later)
  const cardRefs = {
    aov: useRef<HTMLDivElement>(null),
    orderVolume: useRef<HTMLDivElement>(null),
    rtoPerc: useRef<HTMLDivElement>(null),
    prepaidPerc: useRef<HTMLDivElement>(null),
    topPincode: useRef<HTMLDivElement>(null),
    emergingCities: useRef<HTMLDivElement>(null),
  };

  useEffect(() => {
    if (!animatedDivRef.current) return;
    const t = setTimeout(() => setAnimationClass(""), 1200);
    return () => clearTimeout(t);
  }, []);

  const mapRef = useRef<any>(null);

  if (isLoading) return <Loader variant="map-board"/>;

  return (
    <div className="w-full">
      <div ref={animatedDivRef} className={animationClass}>
        {/* Mobile-first: no extra top padding/margins */}
        <div className="w-full">
          {/* 2-column on md+, stacked on mobile; minimal gap */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            {/* LEFT: Map — sticky only on md+ */}
            <div className="md:col-span-5 col-span-12 md:sticky md:top-16 self-start">
              {/* Remove outer spacing; map handles its own sizing */}
              <div className="w-full">
                <MapComponent data={geoData[currTab]} mapRef={mapRef} />
              </div>
            </div>

            {/* RIGHT: Tabs + tables */}
            <div className="md:col-span-7 col-span-12">
              <Tabs
                value={currTab}
                onChange={(_e, v) => setCurrTab(v)}
                aria-label="geo-tabs"
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  width: "100%",
                  minHeight: 36,
                  "& .MuiTabs-flexContainer": { gap: 0.5 },
                  "& .MuiTabs-indicator": {
                    height: 2,
                    background:
                      "linear-gradient(90deg, #FFFFFF 0%, #7676DB 129.61%)",
                  },
                  color: "white",
                  "& .MuiTab-root": {
                    color: "white",
                    textTransform: "none",
                    minHeight: 36,
                    padding: "6px 10px",
                    "&.Mui-selected": { color: "white" },
                  },
                }}
              >
                {TAB_LIST.map((t) => (
                  <Tab key={t.id} value={t.id} label={t.label} />
                ))}
              </Tabs>

              <Stack gap={1.5} sx={{ mt: 1.5 }}>
                {currTab === "citytier_aov" && (
                  <CardHolder
                    divref={cardRefs.aov}
                    id="citytier_aov"
                    title="Top Cities by AOV"
                    description={TABLE_DESCRIPTIONS.aov}
                  >
                    <TableData
                      data={geoData?.citytier_aov}
                      tierData
                      showDownload={
                        geoData?.citytier_aov?.tier1?.length - 5 || false
                      }
                      unit="Rs."
                      unitPosition="left"
                      name="Top cities by AOV"
                    />
                  </CardHolder>
                )}

                {currTab === "citytier_order_volume" && (
                  <CardHolder
                    id="citytier_order_volume"
                    divref={cardRefs.orderVolume}
                    title="Top Cities by Order Volume"
                    description={TABLE_DESCRIPTIONS.order_volume}
                  >
                    <TableData
                      data={geoData?.citytier_order_volume}
                      tierData
                      showDownload={
                        geoData?.citytier_order_volume?.tier1?.length - 5 ||
                        false
                      }
                      name="Top cities by order volume"
                    />
                  </CardHolder>
                )}

                {currTab === "citytier_rto_percentage" && (
                  <CardHolder
                    divref={cardRefs.rtoPerc}
                    title="Top Cities by RTO%"
                    description={TABLE_DESCRIPTIONS.rto_perc}
                    id="citytier_rto_percentage"
                  >
                    <TableData
                      data={geoData?.citytier_rto_percentage}
                      tierData
                      showDownload={
                        geoData?.citytier_rto_percentage?.tier1?.length - 5 ||
                        false
                      }
                      unit="%"
                      unitPosition="right"
                      name="City tier RTO percentage"
                    />
                  </CardHolder>
                )}

                {currTab === "citytier_prepaid_percentage" && (
                  <CardHolder
                    divref={cardRefs.prepaidPerc}
                    title="Top Cities by Prepaid %"
                    description={TABLE_DESCRIPTIONS.prepaid_perc}
                    id="citytier_prepaid_percentage"
                  >
                    <TableData
                      data={geoData?.citytier_prepaid_percentage}
                      tierData
                      showDownload={
                        geoData?.citytier_prepaid_percentage?.tier1?.length -
                          5 || false
                      }
                      unit="%"
                      unitPosition="right"
                      name="Prepaid percentage trends"
                    />
                  </CardHolder>
                )}

                {currTab === "top_rto_picode" && (
                  <CardHolder
                    divref={cardRefs.topPincode}
                    title="Top RTO Pincodes (Min. 1000 orders in last month)"
                    description={TABLE_DESCRIPTIONS.top_pincode}
                    id="top_rto_picode"
                  >
                    <TableData
                      data={geoData?.top_rto_picode}
                      heading={["Pincode", "Percentage"]}
                      showDownload={
                        geoData?.top_rto_picode?.length - 5 || false
                      }
                      unit="%"
                      unitPosition="right"
                      name="Top rto picode"
                    />
                  </CardHolder>
                )}

                {currTab === "emerging_cities" && (
                  <CardHolder
                    divref={cardRefs.emergingCities}
                    title="Emerging cities by order volume increase MoM"
                    description={TABLE_DESCRIPTIONS.emerging_cities}
                    id="emerging_cities"
                  >
                    <TableData
                      data={geoData?.emerging_cities}
                      heading={["City", "% Increase"]}
                      showDownload={
                        geoData?.emerging_cities?.length - 5 || false
                      }
                      unit="%"
                      unitPosition="right"
                      name="Top emerging cities"
                    />
                  </CardHolder>
                )}
              </Stack>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------- CardHolder / TableData / DownloadModal ----------------------

const CardHolder = (props: {
  title: React.ReactNode;
  description?: React.ReactNode;
  children: React.ReactNode;
  secondaryDescription?: React.ReactNode;
  showDownload?: boolean | number;
  sx?: Record<string, unknown>;
  divref: React.RefObject<HTMLDivElement | null>;
  id: string;
}) => {
  return (
    <Card className="darkBgWithBackdrop" sx={{ minWidth: "50%", ...props.sx }}>
      <CardContent>
        <div
          ref={props.divref}
          data-id={props.id}
          className="flex lg:items-center sm:items-start sx:items-start gap-4 lg:justify-between sm:flex-col xs:flex-col lg:flex-row md:justify-between sm:justify-items-start sx:justify-items-start"
        >
          <span className="flex items-center md:gap-4 gap-2 mb-3">
            {props.title && (
              <h1 className="lg:text-[18px] text-[17px] font-semibold lg:leading-[30px] leading-[24px] text-white">
                {props.title}
              </h1>
            )}
            <Tooltip
              title={props?.description || ""}
              placement="top"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: "rgba(255,255,255,0.1)",
                    color: "#ffffff",
                    border: "1px solid #6e5e7987",
                    padding: "14px",
                    lineHeight: "20px",
                    fontSize: "12px",
                    maxWidth: "350px",
                    backdropFilter: "blur(20px)",
                  },
                },
              }}
              enterTouchDelay={0}
            >
              <HelpOutlineIcon
                fontSize="small"
                sx={{
                  color: "#ffffff",
                  transition: "all 100ms",
                  "&:hover": { color: "#50DFB2" },
                }}
              />
            </Tooltip>
          </span>
        </div>
        {props.children}
        <p className="text-left text-[#737373] text-[12px]">
          {props.secondaryDescription}{" "}
        </p>
      </CardContent>
    </Card>
  );
};

const TableData: React.FC<{
  data: any;
  tierData?: boolean;
  heading?: string[];
  showDownload?: boolean | number;
  unit?: string;
  unitPosition?: "left" | "right";
  name: string;
}> = ({ data, tierData, heading, showDownload, unit, unitPosition, name }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { selectedDateRange, selectedCategory } = useFiltersStore();

  const capitalize = (s: string) =>
    s?.charAt(0)?.toUpperCase() + s?.slice(1)?.toLowerCase();

  const buildRows = (): string[][] => {
    if (!tierData) {
      return (data ?? []).map(({ label, value }: any) => [
        capitalize(label),
        `${unitPosition === "left" ? unit ?? "" : ""}${(value ?? 0).toFixed(
          0
        )}${unitPosition === "right" ? unit ?? "" : ""}`,
      ]);
    }
    const max = Math.max(
      ...(Object.values(data || {}) as any[][]).map((t) => t?.length ?? 0),
      0
    );
    const rows: string[][] = [];
    for (let i = 0; i < max; i++) {
      const row: string[] = [];
      Object.keys(data || {}).forEach((tier) => {
        const city = data[tier]?.[i];
        if (city) {
          row.push(
            capitalize(city.label),
            `${unitPosition === "left" ? unit ?? "" : ""}${(
              city.value ?? 0
            ).toFixed(0)}${unitPosition === "right" ? unit ?? "" : ""}`
          );
        } else {
          row.push("", "");
        }
      });
      rows.push(row);
    }
    return rows;
  };

  const downloadExcel = async () => {
    const [{ default: ExcelJS }, { saveAs }] = await Promise.all([
      import("exceljs"),
      import("file-saver"),
    ]);
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet(name);

    sheet.addRow([`📊 ${name}`]);
    sheet.addRow([
      "Downloaded:",
      new Date().toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }),
    ]);
    sheet.addRow([
      "Filters:",
      `Duration – ${selectedDateRange}`,
      `Category – ${selectedCategory?.label ?? "All"}`,
    ]);
    sheet.addRow([]);

    const headerRowIndex = sheet.rowCount + 1;
    const header: string[] = tierData
      ? Object.keys(data || {}).flatMap((t) => [
          `${t.toUpperCase()} City`,
          "Value",
        ])
      : heading ?? ["City", "Value"];

    sheet.addRow(header);
    buildRows().forEach((r) => sheet.addRow(r));

    const headerRow = sheet.getRow(headerRowIndex);
    headerRow.eachCell((cell) => {
      cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF673AB7" },
      };
      cell.alignment = { vertical: "middle", horizontal: "center" };
    });

    sheet.eachRow((row) => {
      row.eachCell((cell) => {
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });
    });

    sheet.views = [{ state: "frozen", ySplit: headerRowIndex }];
    sheet.columns.forEach((col) => {
      const max = Math.max(
        ...(col.values ?? []).map((v) => (v ? v.toString().length : 10))
      );
      (col as any).width = max + 2;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer], {
        type:
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
      }),
      `${name}.xlsx`
    );
  };

  const [modalOpen, setModalOpen] = useState(false);

  const downloadNow = () => {
    gaService.gaClickOnGeoTrendsDownload(name);
    downloadExcel();
  };

  const downloadCSV = (email: string) => {
    feedbackService
      .submitFeedback({
        type: "DOWNLOAD",
        email,
        uuid: crypto.randomUUID?.() ?? Math.random().toString(36).slice(2),
      })
      .then((res: any) => {
        if (res?.success) {
          enqueueSnackbar(
            "Thanks for downloading. Your download should begin shortly."
          );
          setModalOpen(false);
          localStorage.setItem("emailProvided", "true");
          downloadNow();
        } else {
          enqueueSnackbar(
            "Oops! Some error occured. Try again after sometime.",
            { variant: "error" }
          );
        }
      })
      .catch(() =>
        enqueueSnackbar("Oops! Some error occured. Try again after sometime.", {
          variant: "error",
        })
      );
  };

  const triggerHover = (id: string, type: "mouseover" | "mouseout") => {
    const el = window.document.querySelector(
      `circle[data-id="${id}"]`
    ) as HTMLElement | null;
    if (!el) return;
    if (type === "mouseover") {
      el.style.transform = "scale(2)";
      el.style.transition = "transform 0.3s ease";
    } else {
      el.style.transform = "scale(1)";
    }
    const evt = new MouseEvent(type, {
      bubbles: true,
      cancelable: true,
      view: window,
    });
    el.dispatchEvent(evt);
  };

  return (
    <>
      <TableContainer component={Paper} sx={{ bgcolor: "#091A26" }}>
        <Table
          sx={{
            "& .MuiTableCell-root": {
              color: "white",
              bgcolor:
                "linear-gradient(0deg, #091A26, #091A26),linear-gradient(0deg, rgba(255, 255, 255, 0.14), rgba(255, 255, 255, 0.14))",
            },
            "& .MuiTableRow-root .MuiTableCell-root": {
              backgroundColor: "#1D1930",
            },
            "& .MuiTableBody-root .MuiTableRow-root .MuiTableCell-root": {
              borderRight: "1px solid #2C2647",
              p: tierData ? 0.5 : 2,
            },
            "& .MuiTableRow-root ": { borderBottom: "1px solid #2C2647" },
          }}
        >
          <TableHead>
            <TableRow
              sx={{
                "& .MuiTableCell-root": { bgcolor: "#2C2647 !important" },
              }}
            >
              {tierData
                ? Object.keys(data || {}).map((_, idx) => (
                    <TableCell key={idx}>Tier {idx + 1}</TableCell>
                  ))
                : (heading ?? ["City", "Value"]).map((h, i) => (
                    <TableCell key={h} align={i % 2 === 0 ? "left" : "center"}>
                      {h}
                    </TableCell>
                  ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {tierData
              ? (data?.tier1 ?? [])
                  .slice(0, 5)
                  .map((_row: any, index: number) => (
                    <TableRow key={index}>
                      {Object.entries(data || {}).map(
                        ([, tierArr]: any, idx: number) => {
                          const cell = tierArr?.[index];
                          return (
                            <TableCell
                              key={idx}
                              sx={{ width: "33%" }}
                              onMouseOver={() =>
                                cell?.label &&
                                triggerHover(cell.label, "mouseover")
                              }
                              onMouseOut={() =>
                                cell?.label &&
                                triggerHover(cell.label, "mouseout")
                              }
                            >
                              <Box sx={{ p: 0.5 }}>
                                <Tooltip
                                  title={
                                    cell?.label ? capitalize(cell.label) : ""
                                  }
                                  placement="bottom"
                                  componentsProps={{
                                    tooltip: {
                                      sx: {
                                        bgcolor: "#ffffff08",
                                        color: "#ffffff",
                                        border: "1px solid #6e5e7987",
                                        p: 1,
                                        lineHeight: "18px",
                                        fontSize: "12px",
                                        maxWidth: "350px",
                                        backdropFilter: "blur(20px)",
                                      },
                                    },
                                  }}
                                  enterTouchDelay={0}
                                >
                                  <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    sx={{
                                      width: "100%",
                                      p: 1,
                                      borderRadius: 1,
                                      "&:hover": {
                                        backgroundColor: "#FFFFFF1A",
                                      },
                                    }}
                                  >
                                    <span className="text-ellipsis overflow-hidden line-clamp-1">
                                      {cell?.label
                                        ? capitalize(cell.label)
                                        : ""}
                                    </span>
                                    <Typography>
                                      {unitPosition === "left" && unit}
                                      &nbsp;{(cell?.value ?? 0).toFixed(0)}
                                      &nbsp;
                                      {(unitPosition === "right" ||
                                        !unitPosition) &&
                                        unit}
                                    </Typography>
                                  </Stack>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          );
                        }
                      )}
                    </TableRow>
                  ))
              : (data ?? []).slice(0, 5).map((row: any, idx: number) => (
                  <TableRow
                    key={idx}
                    onMouseOver={() =>
                      row?.label && triggerHover(row.label, "mouseover")
                    }
                    onMouseOut={() =>
                      row?.label && triggerHover(row.label, "mouseout")
                    }
                  >
                    <TableCell sx={{ p: 2 }}>
                      {capitalize(row?.label)}
                    </TableCell>
                    <TableCell sx={{ p: 2 }}>
                      <Stack direction="row" justifyContent="center">
                        {unitPosition === "left" && unit}&nbsp;
                        {(row?.value ?? 0).toFixed(0)}&nbsp;
                        {(unitPosition === "right" || !unitPosition) && unit}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </TableContainer>

      {showDownload && parseInt(String(showDownload)) > 5 && (
        <Stack
          direction="row-reverse"
          alignItems="center"
          sx={{ mt: 2 }}
          gap={2}
        >
          <Button
            variant="contained"
            onClick={() => {
              const flag = localStorage.getItem("emailProvided");
              if (!flag || flag === "false" || flag === "null") {
                setModalOpen(true);
              } else {
                downloadNow();
              }
            }}
          >
            <CloudDownloadIcon sx={{ mr: 2 }} /> Download
          </Button>
          <Typography sx={{ color: "white" }} variant="body2">
            {showDownload} More Rows
          </Typography>
        </Stack>
      )}

      <DownloadModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        downloadNow={downloadCSV}
        downloadTableName={name}
      />
    </>
  );
};

interface DownloadModalProps {
  isOpen: boolean;
  onClose: () => void;
  downloadNow: (email: string) => void;
  downloadTableName: string;
}
const DownloadModal: React.FC<DownloadModalProps> = ({
  isOpen,
  onClose,
  downloadNow,
  downloadTableName,
}) => {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");

  const validateEmail = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

  const handleDownloadClick = () => {
    if (!email) return setEmailError("Email address is required.");
    if (!validateEmail(email))
      return setEmailError("Please enter a valid email address.");
    setEmailError("");
    downloadNow(email);
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Download {downloadTableName}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme: any) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" gutterBottom>
          Subscribe with your email address to download data and receive weekly
          updates.
        </Typography>
        <TextField
          fullWidth
          placeholder="Please fill your email address"
          variant="outlined"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setEmailError("");
          }}
          error={!!emailError}
          helperText={emailError}
          sx={{ mt: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Button
          fullWidth
          variant="contained"
          color="primary"
          onClick={handleDownloadClick}
          sx={{
            fontWeight: "bold",
            fontSize: "1rem",
            bgcolor: "#492FBB",
            "&:hover": { bgcolor: "#492FBB" },
          }}
        >
          Download Now
        </Button>
      </DialogActions>
    </Dialog>
  );
};
